import {PLAYER_BONUS_CONFIG, PLAYER_BONUS_TYPES} from "./playerBonus";
import {LEGACY_PLAYER_BONUS_CONFIG, PER_CITY_ONLY_PLAYER_BONUS_CONFIG} from "./playerBonusLegacy";
import {UNITS_SAME_TIME_COUNT_LIMIT} from "./units";
import {FINAL_ROUND} from "./game";

/**
 * Поведение ботов, играющих с ЭКОНОМИЧЕСКИМ БОНУСОМ (см. configs/playerBonus):
 * боты локальной игры, которым бонус выставил человек, и «явные» боты
 * рейтинговых матчей (см. configs/visibleRankedBots).
 *
 * Зачем: живой тест показал перекос богатого бота. Лишние монеты и действия
 * строительства он вкладывал в дороги и базовые кварталы по всей карте —
 * в том числе вплотную к чужим городам, — но НЕ ставил там казарм и ферм,
 * то есть не мог ни нанять гарнизон, ни прокормить его. Армия при этом
 * оставалась у стартового города: тяга к прифронтовому городу включается
 * только у крупных отрядов, а раздача бонусных монет в казармы новых городов
 * не шла вовсе. Итог — россыпь беззащитных построек и пассивная армия.
 *
 * Все надбавки — ВЕСА, а не правила: обычная логика архетипа, снабжения и
 * безопасности остается на месте, меняется только то, что бот предпочтет при
 * прочих равных.
 *
 * ЦЕНА. Замер на ботах против ботов: пакет надбавок стоит бонусному боту
 * винрейта (80% -> 60% на великом бонусе), очки при этом не проседают
 * (60.2 -> 60.0). Так и должно быть: действия строительства уходят из новых
 * кварталов (очки) в казармы и фермы (оборона), а армия у фронта чаще
 * дерется и дает сопернику поводы для размена. Замер бот-против-бота этой
 * цены и стоит: соперник-бот беззащитный город наказывает плохо, живой игрок —
 * отлично, а ради живого игрока бонусные боты и существуют.
 *
 * Ботов БЕЗ бонуса это не касается вовсе (интенсивность 0), поэтому рейтинговые
 * «неявные» боты, боты кампаний и обучения играют ровно как раньше.
 */

// Сколько городов в среднем «весит» доход бонуса за каждый город (см.
// configs/playerBonus) в шкале ниже. 2.25 — середина точек, где фиксированный
// доход уровня до 29.10 равен доходу «только за города»: серебро 2 / 1 (2 города),
// золото 3 / 2 (1.5), алмаз 6 / 3 (2), рубин 10 / 4 (2.5). Числа известных таблиц
// в шкалу идут через числа «только за города» (toBehaviorScaleBonusValues), так
// что коэффициент работает напрямую только на них и на незнакомых числах
export const BOT_BONUS_PER_CITY_INCOME_SCALE = 2.25;

// Суммарный размер бонуса: разнородные прибавки складываются как грубая мера
// «насколько бот богаче обычного». Точность тут не нужна — число служит только
// шкалой, по которой надбавки нарастают от младшего бонуса к старшему. Доход —
// в обеих формах: фиксированный (числа старых записей) и за каждый город
const calcBonusTotal = (bonusValues) => (
  (bonusValues.startingCoinsBonus || 0)
  + (bonusValues.coinsIncomeBonus || 0)
  + (bonusValues.coinsIncomePerCityBonus || 0) * BOT_BONUS_PER_CITY_INCOME_SCALE
  + (bonusValues.constructionActionsBonus || 0)
);

// Числа, на которых настроена шкала надбавок (волны 33–38 мерили поведение на
// них): «доход только за города». Правка экономики уровней в
// configs/playerBonus поведения ботов не меняет — числа уровня сводятся к этим
const BEHAVIOR_SCALE_BONUS_CONFIG = PER_CITY_ONLY_PLAYER_BONUS_CONFIG;

// Таблицы, по числам которых узнается уровень бонуса: текущие и прежние
const KNOWN_BONUS_CONFIGS = [PLAYER_BONUS_CONFIG, PER_CITY_ONLY_PLAYER_BONUS_CONFIG, LEGACY_PLAYER_BONUS_CONFIG];
const BONUS_NUMBER_KEYS = [
  'startingCoinsBonus', 'coinsIncomeBonus', 'coinsIncomePerCityBonus', 'constructionActionsBonus',
  'winPointsIncomeBonus', 'winPointsIncomePerCityBonus', 'd6MinRoll',
];
const checkIfSameBonusNumbers = (bonusValues, expected) => Boolean(expected)
  && BONUS_NUMBER_KEYS.every((key) => (bonusValues[key] || 0) === (expected[key] || 0));

// Числа уровня в шкале надбавок: уровень узнается по числам любой таблицы и
// заменяется числами шкалы. Незнакомые числа (и SMALL, которого в шкале нет)
// идут в шкалу как есть
const toBehaviorScaleBonusValues = (bonusValues) => {
  const bonusType = Object.keys(BEHAVIOR_SCALE_BONUS_CONFIG).find((type) => KNOWN_BONUS_CONFIGS
    .some((config) => checkIfSameBonusNumbers(bonusValues, config[type])));
  return bonusType ? BEHAVIOR_SCALE_BONUS_CONFIG[bonusType] : bonusValues;
};

// 100% интенсивности — рубиновый уровень GREAT. Не максимум конфига: у
// «божества» доход за город больше, но поведенческие надбавки у него те же,
// что у рубина (его отличие — очки к доходу и кости)
const MAX_BONUS_TOTAL = calcBonusTotal(BEHAVIOR_SCALE_BONUS_CONFIG[PLAYER_BONUS_TYPES.GREAT]);

/**
 * Интенсивность надбавок: доля этого бонуса от рубинового (GREAT), не больше 1.
 * 0 — бонуса нет (поведение обычного бота), 1 — рубин и «божество».
 *
 * Считается от ЧИСЕЛ бонуса, а не от его названия: у «явных» ботов числа
 * заморожены на старте матча (см. getPlayerBonusValues), и надбавка обязана
 * соответствовать тому бонусу, который бот реально получил. Числа уровня
 * сводятся к шкале «только за города» (toBehaviorScaleBonusValues): уровень с
 * текущими и с прежними числами получает одни и те же надбавки.
 */
export const getBotBonusIntensity = (bonusValues) => {
  if (!bonusValues || MAX_BONUS_TOTAL <= 0) {
    return 0;
  }
  return Math.min(1, Math.max(0, calcBonusTotal(toBehaviorScaleBonusValues(bonusValues)) / MAX_BONUS_TOTAL));
};

// Прибавка к множителям агрессии архетипа при полной интенсивности: военные
// веса (бой, казармы, коридоры) и жадность до захвата чужих городов.
// Множители, а не подмена архетипа: оборонительный бот с бонусом остается
// оборонительным по радиусу экспансии и накоплению армии, но дерется охотнее.
//
// Держатся НАМЕРЕННО скромными. Замер (server/scripts/tmpBonusBotBehaviorProbe.js,
// 40 партий на общих сидах, люди, 4 игрока, великий бонус у первого):
//   0.2/0.25 — 15.8 боевых контакта за партию, 60 очков, винрейт 60%;
//   0.5/0.6  — 17.3 контакта, 56 очков, винрейт 55%.
// Дальше рост агрессии окупается только зрелищем: бот лезет в размены, которые
// проигрывает, и дарит соперникам очки
export const BOT_BONUS_MILITARY_GAIN = 0.2;
export const BOT_BONUS_CAPTURE_GAIN = 0.25;

// Казарма/ферма в СВОЕМ городе, где такого строения еще нет. Это и есть лечение
// главного перекоса: веса заметно выше базовых BARRACKS_NEEDED (8) и
// FARM_NEEDED (9), потому что конкурировать приходится с дорогами и новыми
// кварталами, которые богатому боту тоже по карману.
//
// Замер (тот же прогон): доля своих городов без казармы падает с 70% до 49%,
// без фермы — с 41% до 27%.
//
// Надбавка адресуется ВСЕМ своим городам, а не только прифронтовым: пробная
// версия, ограниченная городами в 6 ячейках от живого противника, оказалась
// хуже базы по всем метрикам сразу (без фермы 44%, винрейт 47.5%) — тыловые
// города так и оставались пустыми, а действия все равно уходили из кварталов
export const BOT_BONUS_UNDEFENDED_CITY_BARRACKS_SCORE = 20;
export const BOT_BONUS_UNDEFENDED_CITY_FARM_SCORE = 16;

// Прибавка к тяге простаивающей армии в прифронтовый город
// (BOT_WEIGHTS.IDLE_ARMY_FRONTIER_PER_CELL) при полной интенсивности.
// Замер: средняя дистанция отрядов до ближайшего чужого квартала 4.77 -> 4.52
export const BOT_BONUS_FRONTIER_PULL_GAIN = 1;

// С какого размера отряд бонусного бота вообще подхватывает эту тягу. Обычным
// ботам нужен размер 8 (или половина максимума у части архетипов) — у бонусного
// к фронту идут и средние отряды, иначе они так и стоят в тылу
export const BOT_BONUS_FRONTIER_PULL_MIN_UNIT_SIZE = 3;

// ---------------------------------------------------------------------------
// Волна 33: «сильные города» бонусных ботов LARGE («огромный», алмаз) и
// GREAT («великий», рубин).
//
// Живой тест: с 5-8 действиями строительства и +6/+9 дохода бот застраивал
// всю карту городами ради ключевых точек, оставляя фронтирные города без
// гарнизона и с пачкой ферм — живой игрок забирал такой город и получал сразу
// очки и снабжение. Армия при этом не росла: защита лидерства (проекция счета
// с бонусным доходом) уводила бота в DEFENSIVE с середины партии.
//
// Пакет ниже — веса и пороги, гейтированные полем профиля strongCities
// (см. getBotBonusProfile): только боты с интенсивностью ≥ порога, то есть
// ровно LARGE/GREAT; BIG/MEDIUM/SMALL и legacy-группа A/B играют прежним
// пакетом дословно.
//
// Три опоры (см. utils/bots/strategy/bonusAxis.js):
//  - ось удара: ближайший живой противник + город-«кузница» (staging) +
//    тыловой хаб (rear); кузница — кластер казарм/лагерь/арсенал, тыл —
//    склад с кольцом ферм;
//  - классификация городов «тыл/фронт» по BFS до кварталов живых противников;
//  - армейская фаза: первые 2/3 партии ресурсы идут в армию и давление на
//    ближайшего противника, новые города — только у сформированных отрядов.
// ---------------------------------------------------------------------------

// Порог интенсивности бонуса, с которого включается пакет «сильных городов»:
// интенсивность уровня LARGE (алмаз) — его и GREAT пакет включает, BIG — нет.
//
// Интенсивность считается от ЧИСЕЛ бонуса (см. getBotBonusIntensity), поэтому
// порог обязан оставаться между BIG и LARGE при любой правке баланса в
// configs/playerBonus. Прежние 0.7 были подобраны под LARGE 24/33 ≈ 0.73:
// когда бонусы снизили, алмазные боты молча выпали из пакета целиком; потом
// порог был числом 0.5. С 29.10 он считается от чисел LARGE, как пороги волн
// 35 и 36 — от BIG. Числа любой известной таблицы сводятся к шкале «только за
// города»: LARGE 17.75/27 ≈ 0.66, BIG 12.5/27 ≈ 0.46 — и у текущих чисел, и у
// прежних. Состав уровней держит тест (utils/bots/getBotBonusProfile.test.js,
// configs/botBonusBehavior.test.js). Допуск — от погрешности деления
export const BOT_BONUS_STRONG_CITIES_MIN_INTENSITY = getBotBonusIntensity(
  PLAYER_BONUS_CONFIG[PLAYER_BONUS_TYPES.LARGE]
) - 1e-9;

// Доля партии, в которой бонусный бот вкладывается в армию и давит соседа
// (последний раунд армейской фазы = floor(FINAL_ROUND × доля) = 8 при 13)
export const BOT_BONUS_ARMY_PHASE_SHARE = 2 / 3;
export const getBonusArmyPhaseLastRound = () => Math.floor(FINAL_ROUND * BOT_BONUS_ARMY_PHASE_SHARE);
export const isBonusArmyPhase = (round) => round <= getBonusArmyPhaseLastRound();

// Город фронтирный, если его кварталы в этой BFS-дистанции от кварталов живых
// противников (нейтралы не в счет). Тыловой хаб фронтирным не бывает никогда
export const BOT_BONUS_FRONTIER_CITY_MAX_ENEMY_DISTANCE = 6;

// Фермы фронтирного города: ровно одна (звено цепочки снабжения тыл → фронт);
// кузнице — две: там стоит армия, и одной фермы ей не хватало. Калибровка
// (отчет 30, 1 GREAT среди обычных, 4p): с капом 1 голодание +1.9 п.п. против
// legacy, с капом 2 — +1.2 при тех же фермах во фронтире и потерях городов
export const BOT_BONUS_FRONTIER_FARM_CAP = 1;
export const BOT_BONUS_STAGING_FARM_CAP = 2;
// Ферма-ретранслятор ставится подальше от врага (иначе ее гасит один отряд и
// цепочка рвется): +1 за ячейку дистанции до чужих кварталов, до 6
export const BOT_BONUS_FRONTIER_FARM_ENEMY_DISTANCE_PER_CELL = 1;
// Надбавка «город без фермы» во фронтирном городе режется вдвое, чтобы первые
// фермы не разбегались по фронту
export const BOT_BONUS_FRONTIER_UNDEFENDED_FARM_FACTOR = 0.5;

// Тыловой кластер снабжения: ферма у склада (реального или запланированного) и
// у квартала-хаба, пока суммарная емкость ферм тылового города ниже цели
export const BOT_BONUS_REAR_FARM_NEAR_WAREHOUSE = 5;
export const BOT_BONUS_REAR_FARM_HUB = 2;
export const BOT_BONUS_REAR_CLUSTER_CAPACITY_TARGET = UNITS_SAME_TIME_COUNT_LIMIT + 1;
// Склад в тыловом городе при ≥2 смежных фермах
export const BOT_BONUS_REAR_WAREHOUSE_BOOST = 14;
// Дорога на пути «фермы тыла → кварталы кузницы», пока кузница вне досягаемости
// снабжения тыла (цепочка ферм идет только по непустым ячейкам)
export const BOT_BONUS_SUPPLY_CHAIN_ROAD = 10;

// Кузница: до 3 казарм, лагерь и арсенал рядом с ними, кварталы под докупку
// размера (с каждого квартала города по разу за фазу, ×2 при арсенале)
export const BOT_BONUS_FORGE_BARRACKS_CAP = 3;
export const BOT_BONUS_FORGE_BARRACKS_SCORE = 12;
export const BOT_BONUS_BARRACKS_NEAR_CAMP = 5;
export const BOT_BONUS_FORGE_CAMP_BOOST = 12;
export const BOT_BONUS_FORGE_ARSENAL_BOOST = 12;
export const BOT_BONUS_FORGE_PLATFORM_QUARTER = 8;
export const BOT_BONUS_FORGE_QUARTERS_TARGET = 4;
// Кузница переезжает только к городу, который ближе к противнику на столько
// ячеек И уже имеет казарму — иначе кластер размазывался бы по череде городов
export const BOT_BONUS_STAGING_SWITCH_MIN_GAIN = 3;

// Новый город (центральный квартал) ценится по близости к СФОРМИРОВАННЫМ
// отрядам (size ≥ BOT_BONUS_FRONTIER_PULL_MIN_UNIT_SIZE): дальше 3 — ×0.5,
// дальше 5 — ×0.2; там, где враг сильнее в радиусе 3, — ×0.3 (город-подарок).
//
// Штраф действует ТОЛЬКО В ЗОНЕ РИСКА — там, где до кварталов живых
// противников не дальше BOT_BONUS_NEW_CITY_RISK_ZONE_DISTANCE. Замер по
// прод-реплеям (539 партий, отчёт 31): вероятность отдать город человеку
// зависит от дистанции до ПРОТИВНИКА (≤4 — 35%, 5-6 — 29%, 7-9 — 24%,
// дальше 9 — 5.5%), а от дистанции до своей армии не зависит вовсе
// (≤3 — 28.5%, 4-5 — 26.5%, дальше 5 — 29.6%). Без этого гейта штраф резал
// безопасные тыловые города сильнее, чем те, что бот реально терял: армия
// бонусного бота стоит у кузницы, и тыл для неё всегда «далеко».
export const BOT_BONUS_NEW_CITY_UNIT_DISTANCE_SOFT = 3;
export const BOT_BONUS_NEW_CITY_UNIT_DISTANCE_HARD = 5;
export const BOT_BONUS_NEW_CITY_UNIT_DISTANCE_SOFT_FACTOR = 0.5;
export const BOT_BONUS_NEW_CITY_UNIT_DISTANCE_HARD_FACTOR = 0.2;
export const BOT_BONUS_NEW_CITY_OUTWEIGHED_FACTOR = 0.3;
export const BOT_BONUS_NEW_CITY_RISK_ZONE_DISTANCE = 6;

// Армейская фаза профиля: ключевые точки дешевле, захват не ниже единицы
export const BOT_BONUS_ARMY_PHASE_KEY_POINT_FACTOR = 0.8;
export const BOT_BONUS_ARMY_PHASE_CAPTURE_EAGERNESS_MIN = 1;

// Давление на ближайшего противника в перемещении: множитель захвата/
// блокировки/размена против него (при полной интенсивности) и доля общего
// градиента к его кварталам. Стрельба и бой фокус не читают (сознательно)
export const BOT_BONUS_TARGET_ENEMY_GAIN = 0.3;
export const BOT_BONUS_TARGET_ENEMY_APPROACH_WEIGHT = 0.6;

// Гарнизон фронтирного города: единственный держатель квартала без кампании,
// размером ниже доли максимума своего типа; угроза — чужой отряд в радиусе
export const BOT_BONUS_FRONTIER_GARRISON_HOLD = 10;
export const BOT_BONUS_GARRISON_SIZE_SHARE = 0.6;
export const BOT_BONUS_GARRISON_THREAT_RADIUS = 4;

// Стройка в армейской фазе оставляет монеты на два отряда (кап), политики — тоже.
// Волна 35 (S): вторая цена отряда держится только из монет сверх минимального
// бюджета стройки — calcArmyPhaseReserveWithBuildBudget в configs/botBuildRisk
// (прежний резерв ниже — у legacy-группы isPreW35ArmyReservePlayer)
export const BOT_BONUS_ARMY_PHASE_COIN_RESERVE_UNITS = 2;
export const BOT_BONUS_ARMY_PHASE_COIN_RESERVE_MAX = 16;
export const calcBonusArmyPhaseCoinReserve = (bonusProfile, ownUnitsCount, preferredUnitPrice = 8) => (
  bonusProfile && ownUnitsCount < UNITS_SAME_TIME_COUNT_LIMIT
    ? Math.min(BOT_BONUS_ARMY_PHASE_COIN_RESERVE_MAX, BOT_BONUS_ARMY_PHASE_COIN_RESERVE_UNITS * preferredUnitPrice)
    : 0
);

// Фокус роя (крысолюды/инсектоиды): город ближайшего противника предпочитается
// нейтралу, если он не дальше его больше чем на столько ячеек
export const BOT_BONUS_SWARM_FOCUS_ENEMY_EXTRA_DISTANCE = 3;
